import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { Meta } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { NavigationStart, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private baseUrl = `${environment.baseUrl}`;
  currentMarket:string;
  isLoading = false;
  private config: any;

  constructor(
    private http: HttpClient,
    private meta: Meta,
    private router: Router
  ) {}

  loadConfig(): Observable<any> {
    return this.http.get(`${this.baseUrl}/company/config`).pipe(
      tap(res => {
        this.config = res.data;
        console.log('App config loaded:', this.config);
        localStorage.setItem("companyConfig", JSON.stringify(res.data))
        localStorage.setItem("companyMarkets", JSON.stringify(res.data.market))
        const defaultMarket = res.data.market.find(x => x.countryName === 'Nigeria');
        localStorage.setItem("defaultMarket", JSON.stringify(defaultMarket))
        //Setup Website and Company Data
        this.getCompanyConfig(this.config);

        this.setFavicon(this.config.company.main_favicon_url);
        this.setLinkHref(this.config.company.main_logo_url);

        this.meta.updateTag({ name: 'description', content: this.config.company.description });
        this.meta.updateTag({ name: 'keywords', content: this.config.company.keywords });
        
        // Open Graph meta tags
        this.meta.updateTag({ property: 'og:title', content: this.config.company.name });
        this.meta.updateTag({ property: 'og:type', content: this.config.company.main_domain });
        this.meta.updateTag({ property: 'og:url', content: this.config.company.main_domain });
        this.meta.updateTag({ property: 'og:image', content: this.config.company.main_logo_url });
        this.meta.updateTag({ property: 'og:description', content: this.config.company.description });

        // Twitter meta tags
        this.meta.updateTag({ name: 'twitter:site', content: this.config.twitterSite });
        this.meta.updateTag({ name: 'twitter:creator', content: this.config.twitterCreator });
        this.meta.updateTag({ name: 'twitter:title', content: this.config.company.name });
        this.meta.updateTag({ name: 'twitter:description', content: this.config.company.description });
        this.meta.updateTag({ name: 'twitter:image', content: this.config.company.main_logo_url });

        //SEO Tags
        this.meta.updateTag({ name: 'google-site-verification', content: this.config.company.keys.integrations.gSiteVerify });
        this.meta.updateTag({ name: 'facebook-domain-verification', content: this.config.company.keys.integrations.facebookDomainVerification });

        this.addGoogleTagManager(this.config.company.keys.integrations.gtmId);
        this.addGoogleAnalytics(this.config.company.keys.integrations.gtagId);
        this.addGtmNoScript(this.config.company.keys.integrations.gtmId);
        this.addCookieConsent(this.config.company.keys.integrations.cookieConsentUrl);
        this.addSmartech(this.config.company.keys.integrations.smartechKey, this.config.company.keys.integrations.smartechReg);
        this.addGoogleMapsScript({ googleMapsKey: this.config.company.keys.integrations.googleMapsKey, googleMapsLibraries: this.config.company.keys.integrations.googleMapsLibraries});
        this.addFacebookPixelNoScript(this.config.company.keys.integrations.facebookPixelCode);
        this.addFacebookPixelScript(this.config.company.keys.integrations.facebookPixelCode);
        this.addSnapchatPixelScript(this.config.company.keys.integrations.snapPixelCode);
      })
    );
  }

  getCompanyConfig(companyInfo) {
    this.setUserMarket(companyInfo.market);  
    document.documentElement.style.setProperty('--primary-color', companyInfo.theme.colors.primary);
    document.documentElement.style.setProperty('--primary-r', companyInfo.theme.colors.rgbPrimary.r);
    document.documentElement.style.setProperty('--primary-g', companyInfo.theme.colors.rgbPrimary.g);
    document.documentElement.style.setProperty('--primary-b', companyInfo.theme.colors.rgbPrimary.b);
    //this.getHomePage();
  }

  public getHomePageContent(userMarket:string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/page/home_page/en-${userMarket}`);
  }

  getHomePage(userMarket:string) {
    this.getHomePageContent(userMarket).subscribe({
      next: (res: any) => {
        console.log('Home Content', res.data)
        localStorage.setItem("homePageContent", JSON.stringify(res.data))
      },
      error: () => {}
    });
  }

  getConfig(): any {
    return this.config;
  }

  private setUserMarket(companyMarkets) {
    const defaultMarket = companyMarkets.find(x => x.countryName === 'Nigeria');
    this.router.events.pipe().subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.isLoading = true;
        let urlSplit = event.url.split('/');

        if(urlSplit.length == 2 ) {
          const market = urlSplit[1];
          const marketExists = companyMarkets.some(x => x.country == market.toLowerCase());
          //If the market is valid, set it or go get the last used market or set to default market
          this.currentMarket = marketExists ? market.toLowerCase() : localStorage.getItem('market') ?? defaultMarket.country 
          //Set localstrogae values for selected market
          this.setStorageMarketValues(this.currentMarket, companyMarkets);
          //if(!marketExists) this.router.navigate([this.currentMarket]);
        }
        else if (urlSplit.length > 2) {
          const urlRef = urlSplit.slice();
          //console.log('Slice URL', urlRef)
          let market: string;
          // Check if the second part of the URL is already an existing market
          const urlAlreadyHasMarket = companyMarkets.some(
            x => x.country.toLowerCase() === urlRef[1]?.toLowerCase()
          );
          if(urlAlreadyHasMarket) {
            market = urlRef[1];
            this.currentMarket = market
            this.setStorageMarketValues(market, companyMarkets);
          }
          else {
            //Get market from local storage or set to default market
            market = localStorage.getItem('market') ?? defaultMarket.country;
            this.currentMarket = market
            this.setStorageMarketValues(market, companyMarkets);
            //Insert the selected market into the url because initial url doesn't have
            urlRef.splice(1, 0, market);
            const newUrl = urlRef.join('/');
            console.log('New URL', [newUrl]);
            //Navigate to the new url
            this.router.navigate([newUrl], { replaceUrl: true }); // replaceUrl prevents infinite loop
          }
        }
      }
    })
  }

  setStorageMarketValues(market, allMarkets) {
    localStorage.setItem('market', market);
    let marketInfo = allMarkets.find(x => x.country == this.currentMarket);
    localStorage.setItem('marketParams', JSON.stringify(marketInfo))
    //console.log('I set the market to', market)
    //this.getHomePage(market);
    if (!localStorage.getItem('homePageContent')) this.getHomePage(market);
  }

  setFavicon(url: string): void {
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/x-icon';
    link.rel = 'icon';
    link.href = url;

    document.getElementsByTagName('head')[0].appendChild(link);
  }

  setLinkHref(imageUrl:string):void {
    const preloadHref = imageUrl;
    if (preloadHref) {
      let linkEl = document.querySelector("link[rel='preload'][as='image']") as HTMLLinkElement;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.rel = 'preload';
        linkEl.as = 'image';
        document.head.appendChild(linkEl);
      }
      linkEl.href = preloadHref;
    }
  }

  private addGoogleMapsScript(googleKeys:any): void {
    const key = googleKeys.googleMapsKey;
    const libs = googleKeys.googleMapsLibraries || 'places';

    if (!key) return;

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?libraries=${libs}&key=${key}`;
    document.head.appendChild(script);
  }

  private addGoogleTagManager(gtmId: string): void {
    if (!gtmId) return;

    (function (w: any, d: Document, s: string, l: string, i: string) {
      w[l] = w[l] || [];
      w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const f = d.getElementsByTagName(s)[0] as HTMLElement;
      const j = d.createElement(s);
      const dl = l != 'dataLayer' ? '&l=' + l : '';
      (j as HTMLScriptElement).async = true;
      (j as HTMLScriptElement).src =
        'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
      f.parentNode!.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', gtmId);
  }

  private addCookieConsent(url: string): void {
    if (!url) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = url;
    document.head.appendChild(script);
  }

  private addGoogleAnalytics(gtagId: string): void {
    if (!gtagId) return;

    // Load gtag.js
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`;
    document.head.appendChild(gaScript);

    // Add the gtag inline config script
    const inlineScript = document.createElement('script');
    inlineScript.async = true;
    inlineScript.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gtagId}');
    `;
    document.head.appendChild(inlineScript);
  }

  private addAddToAny(scriptUrl: string): void {
    if (!scriptUrl) return;

    const script = document.createElement('script');
    script.async = true;
    script.src = scriptUrl;
    document.head.appendChild(script);
  }

  private addSmartech(key: string, reg: string): void {
    if (!key || !reg) return;

    const script = document.createElement('script');
    script.text = `
      smartech("create", "${key}");
      smartech("register", "${reg}");
      smartech("identify", "");
      smartech("dispatch", "page browse", {});
    `;
    document.head.appendChild(script);
  }

  private addGtmNoScript(gtmId: string): void {
    if (!gtmId) return;

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
        height="0" width="0"
        style="display:none;visibility:hidden">
      </iframe>
    `;
    document.body.appendChild(noscript);
  }

  private addFacebookPixelNoScript(pixelCode: string): void {
    if (!pixelCode) return;

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <img height="1" width="1"
        src="https://www.facebook.com/tr?id=${pixelCode}&ev=PageView&noscript=1"
      />
    `;
    document.body.appendChild(noscript);
  }

  private addFacebookPixelScript(pixelCode: string): void {
    if (!pixelCode) return;

    const script = document.createElement('script');
    script.async = true;
    script.text = `
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
      fbq("init", "${pixelCode}");
      fbq("track", "PageView");
    `;
    document.head.appendChild(script);
  }

  private addSnapchatPixelScript(pixelCode: string, userEmail: string = ''): void {
    if (!pixelCode) return;

    const script = document.createElement('script');
    script.async = true;
    script.type = 'text/javascript';
    script.text = `
      (function (e, t, n) {
        if (e.snaptr) return;
        var a = (e.snaptr = function () {
          a.handleRequest
            ? a.handleRequest.apply(a, arguments)
            : a.queue.push(arguments);
        });
        a.queue = [];
        var s = "script";
        var r = t.createElement(s);
        r.async = !0;
        r.src = n;
        var u = t.getElementsByTagName(s)[0];
        u.parentNode.insertBefore(r, u);
      })(window, document, "https://sc-static.net/scevent.min.js");

      snaptr("init", "${pixelCode}", {
        user_email: "${userEmail}"
      });
      snaptr("track", "PAGE_VIEW");
    `;
    document.head.appendChild(script);
  }

}
